import type { AnnotationCanvas } from './canvas'
import { makeHistoryObserver } from './history'
import { AnnotationEventDelegator } from './event-delegator'
import { DRAW_EVENTS } from '@/lib/annotations/constants'
import { debounce } from '@/lib/utils'
import { applyPanZoom } from '@/lib/annotations/image-coord-conversion'
import { uvToScreen } from '@/lib/annotations/site-slide-conversion'
import { __, curry } from 'ramda'

export class AnnotationHistoryManager extends AnnotationEventDelegator {
  private history: HistoryEvent[] = []
  private zoomFactor = 1
  private pan: Point = { x: 0, y: 0 }
  private uv: Point | null = null

  constructor(canvas: AnnotationCanvas, resizeContainer: HTMLElement) {
    super(canvas, resizeContainer)

    // this will handle all the recording of history, all we must do otherwise
    // is just use the history object to redraw on undo and resize events
    this.observe(makeHistoryObserver(this.history))
  }

  override trigger<K extends keyof DrawingObserver>(event: K, ...args: Parameters<FullDrawingObserver[K]>) {
    if (event === 'reset' && !this.history[0]?.points.length) return

    // decorate the drawing event with a zoom factor so that it may be recorded in annotation history
    if (DRAW_EVENTS.includes(event)) {
      ;(args as Parameters<DrawingHook>)[1] = {
        ...this.pan,
        zoom: this.zoomFactor,
        uv: this.uv || undefined,
      }
    }

    super.trigger(event, ...args)
    // Redrawing on "move" means that .stroke() will only be called once per line.
    // Otherwise, we would be redrawing on top every time a new point is added and
    // the antialiasing looks bad.
    if (event === 'undo' || event === 'move') {
      this.queueReplay()
    }
  }

  setZoomFactor(factor: number) {
    if (this.zoomFactor === factor) return
    if (factor > 0) {
      this.zoomFactor = factor
    }
    this.queueReplay()
  }

  setPanCoords(pan: { x: number; y: number }) {
    if (this.pan.x === pan.x && this.pan.y === pan.y) return
    this.pan = pan
    this.queueReplay()
  }

  setUvCoords(uv: { x: number; y: number } | null) {
    if (this.uv?.x === uv?.x && this.uv?.y === uv?.y) return
    this.uv = uv
    if (uv) {
      this.queueReplay()
    }
  }

  copy(ahm: AnnotationHistoryManager) {
    this.zoomFactor = ahm.zoomFactor
    this.pan = ahm.pan
    this.uv = ahm.uv
    return this
  }

  private queueReplay = debounce(() => {
    this.canvas.reset()
    this.replayHistory()
  }, 0)

  protected override handleResize(width: number, height: number) {
    super.handleResize(width, height)
    this.replayHistory()
  }

  private replayHistory() {
    if (!this.history.length) return

    let { strokeStyle, lineWidth } = this.history[0]
    if (lineWidth) lineWidth *= this.zoomFactor
    this.canvas.setup({ strokeStyle, lineWidth })

    // Convert the pan/zoom coordinates from when this line was drawn to the
    // current pan/zoom settings. Coordinates are normalized to (0,0)
    // so we can zoom without worrying about the origin.
    //
    // Note: if UV mode is used and horizontal FOV > 180, we'll have to deal
    // with wraparound lines. Current max horizontal FOV is 120.
    const _applyPanZoom = curry(applyPanZoom)(__, this.pan, this.zoomFactor)
    const _applyUv = this.uv ? curry(uvToScreen)(__, this.uv) : (x: any) => x
    for (const event of this.history) {
      if ((event.lineWidth ?? 0) * this.zoomFactor !== lineWidth || event.strokeStyle !== strokeStyle) {
        lineWidth = event.lineWidth
        if (lineWidth) lineWidth *= this.zoomFactor
        strokeStyle = event.strokeStyle
        this.canvas.setup({ lineWidth, strokeStyle })
      }
      if (event.points.length) {
        const denormalized = event.points.map(_applyUv as (point: Point) => Point).map(_applyPanZoom as (point: Point) => Point)
        this.canvas.start(denormalized[0])
        for (const point of denormalized.slice(1)) {
          this.canvas.move(point)
        }
        this.canvas.stop()
      }
      this.canvas.setup({ lineWidth, strokeStyle })
    }
  }
}
